import React, { FormEvent, useCallback, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import Upload from '../../components/forms/Upload';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import {
  ContentResourceType,
  postContentResource,
  uploadFiles,
} from '../../services/api';
import {
  normalizePodcastEmbedUrl,
  normalizeVideoEmbedUrl,
} from '../../utils/embedUrl';
import { normalizeExternalUrl } from '../../utils/externalUrl';
import { Container, FormCard, Header } from './styles';

type UploadType = 'images' | 'infographics' | 'podcasts';

const imageFormats = [
  { extension: '.jpg', types: 'image/jpeg' },
  { extension: '.png', types: 'image/png' },
  { extension: '.gif', types: 'image/gif' },
];

const infographicFormats = [{ extension: '.zip', types: 'application/zip' }];

const podcastFormats = [
  { extension: '.mp3', types: 'audio/mpeg' },
  { extension: '.m4a', types: 'audio/mp4' },
  { extension: '.aac', types: 'audio/aac' },
  { extension: '.ogg', types: 'audio/ogg' },
  { extension: '.wav', types: 'audio/wav' },
  { extension: '.webm', types: 'audio/webm' },
];

const getUploadType = (type: ContentResourceType): UploadType => {
  if (type === 'infographic') return 'infographics';
  if (type === 'podcast') return 'podcasts';
  return 'images';
};

const getUploadFormats = (
  type: ContentResourceType,
): { extension: string; types: string }[] => {
  if (type === 'infographic') return infographicFormats;
  if (type === 'podcast') return podcastFormats;
  return imageFormats;
};

const CreateResource: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [type, setType] = useState<ContentResourceType>('video');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [source, setSource] = useState('');
  const [alternativeText, setAlternativeText] = useState('');
  const [width, setWidth] = useState('100%');
  const [height, setHeight] = useState('650px');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const interactiveUrl =
    type === 'interactive' ? normalizeExternalUrl(url) : undefined;

  const handleUpload = useCallback(
    async (files: File[]) => {
      if (!user.provider?.id) {
        addToast({
          title: 'Ops ...',
          description: 'Não foi possível identificar o provedor.',
          type: 'error',
        });
        return;
      }

      try {
        setUploading(true);
        const formData = new FormData();
        const isInfographic = type === 'infographic';
        const uploadType = getUploadType(type);
        formData.append(uploadType, files[0]);
        const response = await uploadFiles({
          formData,
          provider_id: user.provider.id,
          type: uploadType,
        });
        const uploadedUrl = isInfographic
          ? response?.data.url
          : response?.data[0]?.url;
        setUrl(uploadedUrl || '');
      } catch (error) {
        addToast({
          title: 'Ops ...',
          description: 'Não foi possível enviar o arquivo.',
          type: 'error',
        });
      } finally {
        setUploading(false);
      }
    },
    [addToast, type, user.provider],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      let normalizedUrl = url.trim();
      if (type === 'video') {
        normalizedUrl = normalizeVideoEmbedUrl(url) || '';
      }
      if (type === 'podcast') {
        normalizedUrl = normalizePodcastEmbedUrl(url) || '';
      }
      if (type === 'interactive') {
        normalizedUrl = normalizeExternalUrl(url) || '';
      }

      if (!title.trim() || !normalizedUrl) {
        addToast({
          title: 'Ops ...',
          description: 'Informe o título e uma URL válida para o recurso.',
          type: 'error',
        });
        return;
      }

      const payload: Record<string, string> = { url: normalizedUrl };
      if (type === 'infographic' || type === 'interactive') {
        payload.width = width;
        payload.height = height;
      }
      if (type === 'image') {
        payload.caption = caption || title;
        payload.source = source;
        payload.alternativeText = alternativeText;
      }

      try {
        setLoading(true);
        await postContentResource({
          title: title.trim(),
          description: description.trim() || undefined,
          type,
          payload,
        });
        addToast({
          title: 'Sucesso!',
          description: 'Recurso criado e disponível na biblioteca.',
          type: 'success',
        });
        history.push('/dashboard/my-resources');
      } catch (error) {
        addToast({
          title: 'Ops ...',
          description: 'Não foi possível criar o recurso.',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      alternativeText,
      caption,
      description,
      height,
      history,
      source,
      title,
      type,
      url,
      width,
    ],
  );

  return (
    <Container className="slideDown">
      <Header>
        <h1>Novo Recurso</h1>
        <div>
          <Link className="secondary" to="/dashboard/my-resources">
            Voltar para recursos
          </Link>
        </div>
      </Header>

      <FormCard onSubmit={handleSubmit}>
        <div className="row">
          <label>
            Tipo do recurso
            <select
              value={type}
              onChange={event => {
                setType(event.target.value as ContentResourceType);
                setUrl('');
              }}
            >
              <option value="video">Videoaula</option>
              <option value="podcast">Podcast</option>
              <option value="infographic">Infográfico</option>
              <option value="image">Imagem</option>
              <option value="interactive">Conteúdo interativo por URL</option>
            </select>
          </label>
          <label>
            Título
            <input
              value={title}
              maxLength={255}
              onChange={event => setTitle(event.target.value)}
              placeholder="Título que aparecerá na biblioteca"
            />
          </label>
        </div>

        <label>
          Descrição (opcional)
          <textarea
            value={description}
            maxLength={2000}
            onChange={event => setDescription(event.target.value)}
          />
        </label>

        {(type === 'image' || type === 'infographic' || type === 'podcast') && (
          <div className="upload">
            <Upload
              onUpload={handleUpload}
              loading={uploading}
              maxSizeMb={type === 'podcast' ? 200 : 50}
              formts={getUploadFormats(type)}
            />
          </div>
        )}

        <label>
          URL do recurso
          <input
            value={url}
            onChange={event => setUrl(event.target.value)}
            placeholder={
              type === 'video' || type === 'podcast'
                ? 'Cole a URL ou o iframe'
                : type === 'interactive'
                ? 'https://exemplo.com/conteudo/'
                : 'Envie o arquivo acima ou informe a URL'
            }
          />
        </label>

        {(type === 'infographic' || type === 'interactive') && (
          <div className="row">
            <label>
              Largura
              <input
                value={width}
                onChange={event => setWidth(event.target.value)}
              />
            </label>
            <label>
              Altura
              <input
                value={height}
                onChange={event => setHeight(event.target.value)}
              />
            </label>
          </div>
        )}

        {type === 'interactive' && interactiveUrl && (
          <div className="interactive-preview">
            <p>
              A pré-visualização depende de o site permitir incorporação. Se ela
              não carregar, confira a página em uma nova aba.
            </p>
            <a href={interactiveUrl} target="_blank" rel="noopener noreferrer">
              Abrir conteúdo em nova aba
            </a>
            <iframe
              src={interactiveUrl}
              title="Pré-visualização do conteúdo interativo"
              width="100%"
              height="480"
              sandbox="allow-downloads allow-forms allow-modals allow-popups allow-same-origin allow-scripts"
              referrerPolicy="no-referrer"
              allowFullScreen
            />
          </div>
        )}

        {type === 'image' && (
          <>
            <label>
              Legenda
              <input
                value={caption}
                onChange={event => setCaption(event.target.value)}
                placeholder="Se estiver vazia, será utilizado o título"
              />
            </label>
            <div className="row">
              <label>
                Fonte
                <input
                  value={source}
                  onChange={event => setSource(event.target.value)}
                />
              </label>
              <label>
                Texto alternativo
                <input
                  value={alternativeText}
                  onChange={event => setAlternativeText(event.target.value)}
                />
              </label>
            </div>
          </>
        )}

        <div className="actions">
          <button type="submit" disabled={loading || uploading}>
            {loading ? 'Salvando...' : 'Criar recurso'}
          </button>
        </div>
      </FormCard>
    </Container>
  );
};

export default CreateResource;
